import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import LinkChorusDialog from '@/components/LinkChorusDialog';
import {
  ArrowLeft, Download, Loader2, Receipt, Calendar, Hash, FileText,
  Building, Trash2, Send, MapPin, AlertTriangle, Pencil, Save, X, Plus, Sparkles,
  Link2, Calculator, FileSignature,
} from 'lucide-react';
import { parseFR } from '@/lib/utils';
import NumInputFR from '@/components/NumInputFR';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => (typeof n === 'number' ? n : parseFloat(n) || 0).toLocaleString('fr-FR', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const STATUT_STYLE = {
  'Émise':           'bg-blue-100 text-blue-700',
  'Envoyée Chorus':  'bg-purple-100 text-purple-700',
  'Payée':           'bg-emerald-100 text-emerald-700',
  'Rejetée':         'bg-red-100 text-red-700',
};

const normalizeLine = (l) => ({
  designation: l.designation || l.description || '',
  description: l.description || '',
  quantite: parseFloat(l.quantite) || 0,
  unite: l.unite || 'U',
  prix_unitaire_ht: parseFloat(l.prix_unitaire_ht ?? l.prix_unitaire) || 0,
  tva_pct: parseFloat(l.tva_pct ?? l.tva_rate) || 20,
  remise_pct: parseFloat(l.remise_pct ?? l.remise) || 0,
});

export default function FactureDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [facture, setFacture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [sendingChorus, setSendingChorus] = useState(false);
  const [pollingChorus, setPollingChorus] = useState(false);

  // Edit mode state
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editBc, setEditBc] = useState('');
  const [editEngagement, setEditEngagement] = useState('');
  const [editDelai, setEditDelai] = useState(30);
  const [editDate, setEditDate] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editLines, setEditLines] = useState([]);
  // Client info edit (SIRET, adresse, etc.) — éditable même hors mode "édition lignes"
  const [editClient, setEditClient] = useState({
    nom: '', siret: '', adresse: '', code_postal: '', ville: '', email: '', telephone: '', type: 'particulier',
  });
  const [showClientEdit, setShowClientEdit] = useState(false);
  const [savingClient, setSavingClient] = useState(false);
  const [refreshingClient, setRefreshingClient] = useState(false);
  // PV de réception — envoi par email
  const [showPvEmail, setShowPvEmail] = useState(false);
  const [pvEmail, setPvEmail] = useState('');
  const [pvMessage, setPvMessage] = useState('');
  const [pvIncludeFacture, setPvIncludeFacture] = useState(false);
  const [sendingPvEmail, setSendingPvEmail] = useState(false);
  // AI Chorus diagnostic
  const [diagnosingChorus, setDiagnosingChorus] = useState(false);
  const [chorusDiag, setChorusDiag] = useState(null);
  // Link existing Chorus invoice
  const [linkOpen, setLinkOpen] = useState(false);
  // Pennylane push
  const [pushingPennylane, setPushingPennylane] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/factures/${id}`);
      setFacture(r.data);
    } catch (e) {
      toast.error("Facture introuvable");
      navigate('/factures');
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Initialize edit state when entering edit mode
  const startEdit = () => {
    setEditBc(facture.numero_bon_commande || '');
    setEditEngagement(facture.numero_engagement || '');
    setEditDelai(facture.delai_paiement_jours || 30);
    setEditDate(facture.date_facture || '');
    setEditNotes(facture.notes_changements || '');
    setEditLines((facture.lignes || []).map(normalizeLine));
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
  };

  const updateEditLine = (idx, key, value) => {
    setEditLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [key]: value } : l)));
  };
  const removeEditLine = (idx) => setEditLines((prev) => prev.filter((_, i) => i !== idx));
  const addEditLine = () => setEditLines((prev) => [...prev, {
    designation: '', description: '', quantite: 1, unite: 'U',
    prix_unitaire_ht: 0, tva_pct: 20, remise_pct: 0,
  }]);

  // Live preview of totals while editing
  const editTotals = useMemo(() => {
    let ht = 0, tva = 0;
    editLines.forEach((l) => {
      const lineHT = (l.quantite || 0) * (l.prix_unitaire_ht || 0) * (1 - (l.remise_pct || 0) / 100);
      ht += lineHT;
      tva += lineHT * ((l.tva_pct || 0) / 100);
    });
    return { total_ht: ht, total_tva: tva, total_ttc: ht + tva };
  }, [editLines]);

  const saveEdit = async () => {
    if (!editBc.trim()) {
      toast.error("Le numéro du bon de commande ne peut pas être vide");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put(`/factures/${id}`, {
        numero_bon_commande: editBc.trim(),
        numero_engagement: editEngagement.trim() || null,
        delai_paiement_jours: Number(editDelai) || 30,
        date_facture: editDate,
        notes_changements: editNotes,
        lignes: editLines,
        // Flag the facture as no longer identical to the source devis
        // since the user explicitly modified it
        identique_au_devis: false,
      });
      setFacture(r.data);
      setEditing(false);
      toast.success('Facture mise à jour');
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
    setSaving(false);
  };

  const sendToChorus = async () => {
    if (!window.confirm("Envoyer définitivement cette facture sur Chorus Pro ?\n(elle ne sera plus modifiable après transmission)")) return;
    setSendingChorus(true);
    try {
      const r = await api.post(`/chorus/factures/${id}/send`);
      setFacture(r.data.facture);
      toast.success(`Facture transmise à Chorus${r.data.facture?.chorus_id ? ` (n° ${r.data.facture.chorus_id})` : ''}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi sur Chorus", { duration: 8000 });
    }
    setSendingChorus(false);
  };

  const refreshChorusStatus = async () => {
    setPollingChorus(true);
    try {
      const r = await api.get(`/chorus/factures/${id}/status`);
      setFacture((prev) => ({ ...prev, chorus_status: r.data.chorus_status }));
      toast.success(`Statut Chorus : ${r.data.chorus_status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de récupérer le statut");
    }
    setPollingChorus(false);
  };

  const runChorusDiagnostic = async () => {
    setDiagnosingChorus(true);
    try {
      const r = await api.post(`/orchestrator/chorus/diagnose`, { facture_id: id });
      setChorusDiag(r.data);
      toast.success("Diagnostic IA généré");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de lancer le diagnostic IA");
    }
    setDiagnosingChorus(false);
  };

  // --- Client info edit & refresh -----------------------------------------
  const openClientEdit = () => {
    const ci = facture?.client_info || {};
    setEditClient({
      nom: ci.nom || ci.nom_prenom || '',
      siret: ci.siret || '',
      adresse: ci.adresse || '',
      code_postal: ci.code_postal || '',
      ville: ci.ville || '',
      email: ci.email || '',
      telephone: ci.telephone || '',
      type: ci.type || 'particulier',
    });
    setShowClientEdit(true);
  };

  const saveClientEdit = async () => {
    setSavingClient(true);
    try {
      const payload = { client_info: { ...editClient } };
      // Normalise SIRET (digits only)
      if (payload.client_info.siret) {
        payload.client_info.siret = String(payload.client_info.siret).replace(/\s/g, '');
      }
      const r = await api.put(`/factures/${id}`, payload);
      setFacture(r.data);
      setShowClientEdit(false);
      toast.success('Informations client mises à jour');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la mise à jour client', { duration: 8000 });
    }
    setSavingClient(false);
  };

  const refreshClientFromCRM = async () => {
    if (!window.confirm('Recharger les informations client depuis la fiche client / contact ? Les champs existants seront écrasés par les valeurs du CRM.')) return;
    setRefreshingClient(true);
    try {
      const r = await api.post(`/factures/${id}/refresh-client-info`);
      setFacture(r.data.facture);
      const src = r.data.source === 'gestion_client' ? 'fiche collectivité' : 'contact';
      toast.success(`Infos rechargées depuis la ${src}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec du rafraîchissement', { duration: 8000 });
    }
    setRefreshingClient(false);
  };

  const openPvEmailModal = () => {
    setPvEmail(facture?.client_info?.email || '');
    setPvMessage('');
    setPvIncludeFacture(false);
    setShowPvEmail(true);
  };

  const sendPvByEmail = async () => {
    if (!pvEmail.trim() || !pvEmail.includes('@')) {
      toast.error('Email invalide');
      return;
    }
    setSendingPvEmail(true);
    try {
      const r = await api.post(`/factures/${id}/pv-reception/send`, {
        to: pvEmail.trim(),
        message: pvMessage.trim() || undefined,
        include_facture: pvIncludeFacture,
      });
      toast.success(`PV envoyé à ${r.data.sent_to}`, { duration: 6000 });
      setShowPvEmail(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi", { duration: 12000 });
    }
    setSendingPvEmail(false);
  };

  const pushPennylane = async () => {
    if (!window.confirm("Envoyer cette facture sur Pennylane (comptabilité) ?")) return;
    setPushingPennylane(true);
    try {      const r = await api.post(`/pennylane/factures/${id}/push`);
      toast.success(`Facture poussée sur Pennylane (n° ${r.data.pennylane_invoice_id})`);
      try { await load(); } catch { /* keep UI */ }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Échec du push Pennylane";
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail), { duration: 10000 });
    }
    setPushingPennylane(false);
  };

  const onDelete = async () => {
    if (!window.confirm("Supprimer définitivement cette facture ? Le devis sera ré-ouvert.")) return;
    setDeleting(true);
    try {
      await api.delete(`/factures/${id}`);
      toast.success("Facture supprimée");
      navigate('/factures');
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div>;
  }
  if (!facture) return null;

  const t = facture.totaux || {};
  const statutClass = STATUT_STYLE[facture.statut] || 'bg-slate-100 text-slate-700';
  const isLocked = ['Envoyée Chorus', 'Payée'].includes(facture.statut);

  return (
    <div className="space-y-5" data-testid="facture-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/factures')} className="p-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
            <Receipt className="w-6 h-6 text-blue-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Facture {facture.numero}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={statutClass}>{facture.statut}</Badge>
              {facture.devis_id && (
                <button
                  onClick={() => navigate(`/devis/${facture.devis_id}`)}
                  className="text-xs text-slate-500 hover:text-blue-600 inline-flex items-center gap-1"
                >
                  <FileText className="w-3 h-3" /> issu du devis {facture.devis_id}
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {editing ? (
            <div key="action-edit" className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" onClick={cancelEdit} disabled={saving}>
                <X className="w-4 h-4 mr-1.5" /> Annuler
              </Button>
              <Button onClick={saveEdit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="facture-save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </div>
          ) : (
            <div key="action-view" className="flex items-center gap-2 flex-wrap">
              {!isLocked && (
                <Button variant="outline" onClick={startEdit} data-testid="facture-edit-btn">
                  <Pencil className="w-4 h-4 mr-1.5" /> Modifier
                </Button>
              )}
              <a
                href={`${BACKEND_URL}/api/factures/${id}/pdf`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                data-testid="facture-pdf-btn"
              >
                <Download className="w-4 h-4" /> Télécharger PDF
              </a>
              <a
                href={`${BACKEND_URL}/api/factures/${id}/pv-reception`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
                data-testid="facture-pv-btn"
                title="Générer un Procès-verbal de réception de chantier pré-rempli (2 pages, prêt à signer)"
              >
                <FileSignature className="w-4 h-4" /> PV de réception
              </a>
              <Button
                variant="outline"
                onClick={openPvEmailModal}
                data-testid="facture-pv-email-btn"
                title="Envoyer le PV de réception par email au client"
              >
                <Send className="w-4 h-4 mr-1.5" /> Envoyer PV par email
              </Button>
              {facture.statut === 'Émise' && (
                <Button
                  variant="outline"
                  onClick={sendToChorus}
                  disabled={sendingChorus}
                  data-testid="facture-chorus-btn"
                >
                  {sendingChorus
                    ? <Loader2 key="ic-sending-chorus" className="w-4 h-4 mr-1.5 animate-spin" />
                    : <Send key="ic-send-chorus" className="w-4 h-4 mr-1.5" />}
                  Envoyer sur Chorus
                </Button>
              )}
              {!facture.pennylane_invoice_id && (
                <Button
                  variant="outline"
                  onClick={pushPennylane}
                  disabled={pushingPennylane}
                  data-testid="facture-pennylane-btn"
                  className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                >
                  {pushingPennylane
                    ? <Loader2 key="ic-pushing-py" className="w-4 h-4 mr-1.5 animate-spin" />
                    : <Calculator key="ic-py" className="w-4 h-4 mr-1.5" />}
                  Envoyer sur Pennylane
                </Button>
              )}
              {facture.pennylane_invoice_id && (
                <Badge key="badge-py" className="bg-emerald-100 text-emerald-700 px-2 py-1">
                  <Calculator className="w-3 h-3 mr-1" />
                  Pennylane #{facture.pennylane_invoice_id}
                </Badge>
              )}
              <Button
                variant="ghost"
                onClick={onDelete}
                disabled={deleting || isLocked}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Key info cards (or editable in edit mode) */}
      {editing ? (
        <div key="info-edit" className="bg-white rounded-xl border border-blue-200 ring-1 ring-blue-100 p-4 space-y-3" data-testid="facture-edit-fields">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                <Hash className="w-3 h-3" /> N° bon de commande
              </label>
              <Input value={editBc} onChange={(e) => setEditBc(e.target.value)} className="mt-1" data-testid="edit-bc-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                <Hash className="w-3 h-3" /> N° d'engagement (Chorus)
              </label>
              <Input value={editEngagement} onChange={(e) => setEditEngagement(e.target.value)} className="mt-1" placeholder="facultatif" data-testid="edit-engagement-input" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Date facture
              </label>
              <Input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} className="mt-1" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Délai paiement (j)</label>
              <Input type="number" value={editDelai} onChange={(e) => setEditDelai(e.target.value)} className="mt-1" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Notes de modification (facultatif)</label>
            <Textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} className="mt-1" rows={2}
              placeholder="Ex : ligne 'Pose carrelage' retirée car réalisée par le client" />
          </div>
        </div>
      ) : (
        <div key="info-view" className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <InfoCard icon={Building} label="Client" value={facture.client_info?.nom || '—'} />
          <InfoCard icon={Calendar} label="Date facture" value={facture.date_facture || '—'} />
          <InfoCard icon={Hash} label="Bon de commande" value={facture.numero_bon_commande || '—'} highlight={!!facture.numero_bon_commande} />
          <InfoCard icon={Hash} label="N° engagement" value={facture.numero_engagement || '—'} highlight={!!facture.numero_engagement} />
          <InfoCard icon={Calendar} label="Délai paiement" value={`${facture.delai_paiement_jours || 30} j`} />
        </div>
      )}

      {/* Chantier address */}
      {facture.chantier_adresse && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
            <MapPin className="w-4 h-4 text-slate-600" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Chantier</p>
            <p className="text-sm text-slate-900 mt-0.5">{facture.chantier_adresse}</p>
          </div>
        </div>
      )}

      {/* Notes de changement (read-only mode) */}
      {!editing && !facture.identique_au_devis && facture.notes_changements && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-xs font-semibold text-amber-800 mb-1">Modifications par rapport au devis</p>
            <p className="text-sm text-amber-900 whitespace-pre-wrap">{facture.notes_changements}</p>
          </div>
        </div>
      )}

      {/* Informations client (éditable, source de vérité pour Chorus + Pennylane + PDF) */}
      {!editing && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="facture-client-info">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <Building className="w-4 h-4 text-slate-500" /> Informations client
            </h3>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={refreshClientFromCRM}
                disabled={refreshingClient || isLocked}
                data-testid="facture-refresh-client-btn"
                className="text-slate-600 hover:bg-slate-50"
                title="Recharger depuis la fiche client (collectivité ou contact)"
              >
                {refreshingClient
                  ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                Rafraîchir depuis le CRM
              </Button>
              <Button
                size="sm"
                onClick={openClientEdit}
                disabled={isLocked}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="facture-edit-client-btn"
              >
                <Pencil className="w-3.5 h-3.5 mr-1.5" />
                Modifier
              </Button>
            </div>
          </div>
          {(() => {
            const ci = facture.client_info || {};
            const hasSiret = !!(ci.siret && String(ci.siret).trim());
            const isPro = (ci.type || '').toLowerCase() === 'professionnel' || hasSiret;
            const fullAddr = [ci.adresse, [ci.code_postal, ci.ville].filter(Boolean).join(' ')].filter(Boolean).join(' · ');
            return (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">Nom / Raison sociale</p>
                  <p className="font-semibold text-slate-900">{ci.nom || ci.nom_prenom || '—'}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{isPro ? 'Professionnel' : 'Particulier'}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">SIRET</p>
                  <p className={`font-mono ${hasSiret ? 'text-slate-900' : 'text-rose-600'}`} data-testid="facture-siret-display">
                    {hasSiret ? ci.siret : '⚠ manquant — requis pour Chorus Pro'}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">Adresse</p>
                  <p className="text-slate-900">{fullAddr || '—'}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">Téléphone</p>
                  <p className="text-slate-900">{ci.telephone || '—'}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">Email</p>
                  <p className="text-slate-900 truncate">{ci.email || '—'}</p>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Modal d'édition des infos client */}
      {showClientEdit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="client-edit-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-slate-900">Modifier les informations client</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowClientEdit(false)} disabled={savingClient}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Nom / Raison sociale *</label>
                  <Input value={editClient.nom} onChange={(e) => setEditClient({...editClient, nom: e.target.value})} className="mt-1" data-testid="client-edit-nom" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Type</label>
                  <select
                    className="mt-1 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                    value={editClient.type}
                    onChange={(e) => setEditClient({...editClient, type: e.target.value})}
                    data-testid="client-edit-type"
                  >
                    <option value="particulier">Particulier</option>
                    <option value="professionnel">Professionnel</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">SIRET (14 chiffres — requis pour Chorus & Pennylane B2B)</label>
                <Input
                  value={editClient.siret}
                  onChange={(e) => setEditClient({...editClient, siret: e.target.value})}
                  className="mt-1 font-mono"
                  placeholder="ex : 21750001600019"
                  inputMode="numeric"
                  data-testid="client-edit-siret"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Adresse</label>
                <Input value={editClient.adresse} onChange={(e) => setEditClient({...editClient, adresse: e.target.value})} className="mt-1" data-testid="client-edit-adresse" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Code postal</label>
                  <Input value={editClient.code_postal} onChange={(e) => setEditClient({...editClient, code_postal: e.target.value})} className="mt-1" data-testid="client-edit-cp" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Ville</label>
                  <Input value={editClient.ville} onChange={(e) => setEditClient({...editClient, ville: e.target.value})} className="mt-1" data-testid="client-edit-ville" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Téléphone</label>
                  <Input value={editClient.telephone} onChange={(e) => setEditClient({...editClient, telephone: e.target.value})} className="mt-1" data-testid="client-edit-tel" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Email</label>
                  <Input type="email" value={editClient.email} onChange={(e) => setEditClient({...editClient, email: e.target.value})} className="mt-1" data-testid="client-edit-email" />
                </div>
              </div>
              <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
                💡 Ces informations sont reprises automatiquement sur le PDF, sur Chorus Pro (SIRET destinataire) et sur Pennylane (création client).
              </p>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowClientEdit(false)} disabled={savingClient}>Annuler</Button>
              <Button
                onClick={saveClientEdit}
                disabled={savingClient || !editClient.nom?.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="client-edit-save"
              >
                {savingClient ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Lignes */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-bold text-slate-900">Lignes de la facture</h2>
          {editing && (
            <Button size="sm" variant="outline" onClick={addEditLine} data-testid="edit-add-line">
              <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une ligne
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left p-3">Désignation</th>
                <th className="text-right p-3 w-20">Qté</th>
                <th className="text-left p-3 w-16">Unité</th>
                <th className="text-right p-3 w-24">PU HT</th>
                <th className="text-right p-3 w-16">TVA</th>
                <th className="text-right p-3 w-14">Rem</th>
                <th className="text-right p-3 w-28">Total HT</th>
                {editing && <th className="w-10"></th>}
              </tr>
            </thead>
            <tbody>
              {(editing ? editLines : (facture.lignes || [])).map((l, i) => {
                const pu = l.prix_unitaire_ht ?? l.prix_unitaire ?? 0;
                const tva = l.tva_pct ?? l.tva_rate ?? 20;
                const remise = l.remise_pct ?? l.remise ?? 0;
                const ht = (l.quantite || 0) * (pu || 0) * (1 - (remise || 0) / 100);
                if (editing) {
                  return (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="p-1.5">
                        <Input value={l.designation} onChange={(e) => updateEditLine(i, 'designation', e.target.value)} className="h-8 text-xs" />
                      </td>
                      <td className="p-1.5"><NumInputFR value={l.quantite} onChange={(n) => updateEditLine(i, 'quantite', n)} className="h-8 text-xs text-right" /></td>
                      <td className="p-1.5"><Input value={l.unite} onChange={(e) => updateEditLine(i, 'unite', e.target.value)} className="h-8 text-xs" /></td>
                      <td className="p-1.5"><NumInputFR value={l.prix_unitaire_ht} onChange={(n) => updateEditLine(i, 'prix_unitaire_ht', n)} className="h-8 text-xs text-right" /></td>
                      <td className="p-1.5"><NumInputFR value={l.tva_pct} onChange={(n) => updateEditLine(i, 'tva_pct', n)} className="h-8 text-xs text-right" /></td>
                      <td className="p-1.5"><NumInputFR value={l.remise_pct} onChange={(n) => updateEditLine(i, 'remise_pct', n)} className="h-8 text-xs text-right" /></td>
                      <td className="p-3 text-right font-semibold">{fmt(ht)} €</td>
                      <td className="p-1.5">
                        <button onClick={() => removeEditLine(i)} className="p-1 text-slate-400 hover:text-red-600">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="p-3">
                      <p className="font-medium text-slate-900">{l.designation}</p>
                      {l.description && <p className="text-xs text-slate-500 mt-0.5">{l.description}</p>}
                    </td>
                    <td className="p-3 text-right">{l.quantite}</td>
                    <td className="p-3 text-slate-500">{l.unite}</td>
                    <td className="p-3 text-right">{fmt(pu)} €</td>
                    <td className="p-3 text-right text-slate-500">{tva}%</td>
                    <td className="p-3 text-right text-slate-500">{remise > 0 ? `${remise}%` : '—'}</td>
                    <td className="p-3 text-right font-semibold">{fmt(ht)} €</td>
                  </tr>
                );
              })}
              {editing && editLines.length === 0 && (
                <tr><td colSpan={8} className="p-4 text-center text-slate-400">Aucune ligne — cliquez sur « Ajouter une ligne »</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="border-t border-slate-200 bg-slate-50 p-4">
          <div className="ml-auto max-w-xs space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Total HT</span><span className="font-medium">{fmt(editing ? editTotals.total_ht : t.total_ht)} €</span></div>
            <div className="flex justify-between"><span className="text-slate-600">TVA</span><span className="font-medium">{fmt(editing ? editTotals.total_tva : t.total_tva)} €</span></div>
            <div className="flex justify-between pt-1.5 border-t border-slate-300">
              <span className="font-bold text-slate-900">Total TTC</span>
              <span className="font-bold text-blue-700 text-lg">{fmt(editing ? editTotals.total_ttc : t.total_ttc)} €</span>
            </div>
          </div>
        </div>
      </div>

      {/* Chorus tracking */}
      {facture.chorus_status ? (
        <div className={`rounded-xl border p-4 ${
          ['REJETEE', 'A_RECYCLER', 'SUSPENDUE'].includes(facture.chorus_status)
            ? 'bg-red-50 border-red-200'
            : 'bg-purple-50 border-purple-200'
        }`} data-testid="facture-chorus-section">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider font-semibold text-purple-700">Suivi Chorus Pro</p>
              <p className="text-sm mt-1">
                Statut : <b>{facture.chorus_status.replaceAll('_', ' ')}</b>
                {facture.chorus_id && <span className="text-purple-700"> · n° {facture.chorus_id}</span>}
                {facture.chorus_env && <span className="text-xs text-purple-500"> · {facture.chorus_env}</span>}
              </p>
              {facture.numero_bon_commande && (
                <p className="text-xs text-purple-700 mt-1">
                  Bon de commande : <b>{facture.numero_bon_commande}</b>
                  {facture.numero_engagement && <span> · Engagement : <b>{facture.numero_engagement}</b></span>}
                </p>
              )}
              {facture.chorus_history && facture.chorus_history.length > 0 && (
                <p className="text-xs text-purple-600 mt-1">
                  {facture.chorus_history.length} événement(s) · dernier : {facture.chorus_history.at(-1)?.ts?.slice(0, 16).replace('T', ' ')}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1.5 items-end shrink-0">
              <Button size="sm" variant="outline" onClick={refreshChorusStatus} disabled={pollingChorus} data-testid="chorus-refresh-btn">
                {pollingChorus ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Actualiser'}
              </Button>
              {['REJETEE', 'A_RECYCLER', 'SUSPENDUE'].includes(facture.chorus_status) && (
                <Button
                  size="sm"
                  onClick={runChorusDiagnostic}
                  disabled={diagnosingChorus}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="chorus-diagnose-btn"
                >
                  {diagnosingChorus ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                  Diagnostic IA
                </Button>
              )}
            </div>
          </div>

          {/* AI diagnostic result */}
          {chorusDiag && (
            <div className="mt-3 bg-white rounded-lg border border-amber-200 p-3 space-y-2" data-testid="chorus-diag-result">
              <p className="text-sm text-slate-900"><b>Diagnostic :</b> {chorusDiag.diagnostic}</p>
              {Array.isArray(chorusDiag.corrections) && chorusDiag.corrections.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Corrections proposées</p>
                  <ul className="space-y-1 text-sm">
                    {chorusDiag.corrections.map((c, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        <span>
                          <b>{c.champ}</b> :{' '}
                          <span className="line-through text-slate-400">{String(c.valeur_actuelle ?? '—')}</span>{' → '}
                          <span className="text-emerald-700 font-semibold">{String(c.valeur_proposee ?? '—')}</span>
                          {c.raison && <span className="text-xs text-slate-500"> ({c.raison})</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {Array.isArray(chorusDiag.etapes) && chorusDiag.etapes.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Étapes</p>
                  <ol className="list-decimal list-inside text-sm space-y-0.5">
                    {chorusDiag.etapes.map((s, i) => <li key={i}>{s}</li>)}
                  </ol>
                </div>
              )}
              <p className="text-xs text-slate-500 italic pt-1">
                {chorusDiag.peut_etre_renvoyee_meme_numero
                  ? 'Cette facture peut être corrigée et renvoyée avec le même numéro.'
                  : 'Cette facture doit être remplacée par une NOUVELLE facture avec un nouveau numéro (utilisez « Dupliquer »).'}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 flex items-center justify-between gap-3" data-testid="facture-chorus-not-sent">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Send className="w-3.5 h-3.5" />
            Cette facture n'a pas encore été transmise à Chorus Pro.
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setLinkOpen(true)}
            data-testid="open-link-chorus-btn"
          >
            <Link2 className="w-3.5 h-3.5 mr-1.5" /> Lier à une facture Chorus existante
          </Button>
        </div>
      )}

      <LinkChorusDialog
        open={linkOpen}
        onOpenChange={setLinkOpen}
        facture={facture}
        onLinked={(f) => setFacture(f)}
      />

      {/* Modal — envoi du PV de réception par email */}
      {showPvEmail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="pv-email-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Send className="w-5 h-5 text-emerald-600" />
                Envoyer le PV de réception
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setShowPvEmail(false)} disabled={sendingPvEmail}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Destinataire *</label>
                <Input
                  type="email"
                  value={pvEmail}
                  onChange={(e) => setPvEmail(e.target.value)}
                  placeholder="exemple@mairie.fr"
                  data-testid="pv-email-to"
                />
                {facture?.client_info?.email && pvEmail !== facture.client_info.email && (
                  <button
                    type="button"
                    className="text-[11px] text-blue-600 hover:underline mt-1"
                    onClick={() => setPvEmail(facture.client_info.email)}
                  >
                    {`Utiliser l'email du client (${facture.client_info.email})`}
                  </button>
                )}
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Message complémentaire (optionnel)</label>
                <textarea
                  value={pvMessage}
                  onChange={(e) => setPvMessage(e.target.value)}
                  className="w-full min-h-[80px] border border-slate-200 rounded-md p-2 text-sm"
                  placeholder="Texte personnalisé à ajouter au message…"
                  data-testid="pv-email-message"
                />
              </div>
              <label className="flex items-start gap-2 cursor-pointer p-2 rounded-md hover:bg-slate-50 border border-slate-200" data-testid="pv-email-include-facture-label">
                <input
                  type="checkbox"
                  checked={pvIncludeFacture}
                  onChange={(e) => setPvIncludeFacture(e.target.checked)}
                  className="mt-0.5"
                  data-testid="pv-email-include-facture"
                />
                <span className="text-sm text-slate-700">
                  <b>Joindre aussi la facture</b> {facture?.numero ? `(${facture.numero})` : ''} au mail
                  <span className="block text-[11px] text-slate-500 mt-0.5">Le destinataire recevra le PV et la facture en pièces jointes.</span>
                </span>
              </label>
              <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
                {"Le PV (2 pages, avec date du jour et tampon Reno Resolve pré-rempli) sera attaché au mail. Le destinataire n'aura qu'à le signer et le retourner."}
              </p>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setShowPvEmail(false)} disabled={sendingPvEmail}>Annuler</Button>
              <Button
                onClick={sendPvByEmail}
                disabled={sendingPvEmail || !pvEmail.trim()}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="pv-email-send"
              >
                {sendingPvEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Envoyer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoCard({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`bg-white rounded-xl border p-4 ${highlight ? 'border-blue-200 ring-1 ring-blue-100' : 'border-slate-200'}`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-3.5 h-3.5 ${highlight ? 'text-blue-600' : 'text-slate-400'}`} />
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      </div>
      <p className="text-sm font-semibold text-slate-900 mt-1 truncate">{value}</p>
    </div>
  );
}
